import type { ServiceRegistry } from "./ServiceProvider";
import type { PolygonClient } from "@/lib/polygon";

// ─── Mock adapters ───────────────────────────────────────
import { MockAIService } from "./adapters/mock/MockAIService";
import { MockAuthService } from "./adapters/mock/MockAuthService";
import { MockQuestService } from "./adapters/mock/MockQuestService";
import { MockQuestTemplateService } from "./adapters/mock/MockQuestTemplateService";
import { MockBountyService } from "./adapters/mock/MockBountyService";
import { MockUserService } from "./adapters/mock/MockUserService";
import { MockPaymentService } from "./adapters/mock/MockPaymentService";
import { MockStorageService } from "./adapters/mock/MockStorageService";
import { MockWorkspaceService } from "./adapters/mock/MockWorkspaceService";
import { MockNegotiationService } from "./adapters/mock/MockNegotiationService";
import { MockAdminService } from "./adapters/mock/MockAdminService";
import { MockStaffingService } from "./adapters/mock/MockStaffingService";
import { MockCandidatesService } from "./adapters/mock/MockCandidatesService";
import { MockComplianceService } from "./adapters/mock/MockComplianceService";
import { MockQuestImageService } from "./adapters/mock/MockQuestImageService";
import { MockAttentionService } from "./adapters/mock/MockAttentionService";
import { MockChannelService } from "./adapters/mock/MockChannelService";
import { MockQuestInstructionsService } from "./adapters/mock/MockQuestInstructionsService";
import { MockBoardService } from "./adapters/mock/MockBoardService";
import { MockOrganizationService } from "./adapters/mock/MockOrganizationService";
import { MockOrgHubService } from "./adapters/mock/MockOrgHubService";
import { MockAgentService } from "./adapters/mock/MockAgentService";
import { MockBadgeService } from "./adapters/mock/MockBadgeService";
import { MockCertificateService } from "./adapters/mock/MockCertificateService";
import { MockExchangeService } from "./adapters/mock/MockExchangeService";
import { MockActivityBadgeService } from "./adapters/mock/MockActivityBadgeService";

// ─── Supabase adapters ──────────────────────────────────
import { SupabaseAdminService } from "./adapters/supabase/SupabaseAdminService";
import { SupabaseStaffingService } from "./adapters/supabase/SupabaseStaffingService";
import { SupabaseAIService } from "./adapters/supabase/SupabaseAIService";
import { SupabaseQuestService } from "./adapters/supabase/SupabaseQuestService";
import { SupabaseQuestTemplateService } from "./adapters/supabase/SupabaseQuestTemplateService";
import { SupabaseBountyService } from "./adapters/supabase/SupabaseBountyService";
import { SupabaseUserService } from "./adapters/supabase/SupabaseUserService";
import { PolygonPaymentService } from "./adapters/supabase/PolygonPaymentService";
import { SupabaseWorkspaceService } from "./adapters/supabase/SupabaseWorkspaceService";
import { SupabaseNegotiationService } from "./adapters/supabase/SupabaseNegotiationService";
import { SupabaseCandidatesService } from "./adapters/supabase/SupabaseCandidatesService";
import { SupabaseComplianceService } from "./adapters/supabase/SupabaseComplianceService";
import { SupabaseQuestImageService } from "./adapters/supabase/SupabaseQuestImageService";
import { SupabaseAttentionService } from "./adapters/supabase/SupabaseAttentionService";
import { SupabaseChannelService } from "./adapters/supabase/SupabaseChannelService";
import { SupabaseQuestInstructionsService } from "./adapters/supabase/SupabaseQuestInstructionsService";
import { SupabaseBoardService } from "./adapters/supabase/SupabaseBoardService";
import { SupabaseOrganizationService } from "./adapters/supabase/SupabaseOrganizationService";
import { SupabaseOrgHubService } from "./adapters/supabase/SupabaseOrgHubService";
import { SupabaseAgentService } from "./adapters/supabase/SupabaseAgentService";
import { SupabaseBadgeService } from "./adapters/supabase/SupabaseBadgeService";
import { SupabaseCertificateService } from "./adapters/supabase/SupabaseCertificateService";
import { SupabaseExchangeService } from "./adapters/supabase/SupabaseExchangeService";
import { SupabaseActivityBadgeService } from "./adapters/supabase/SupabaseActivityBadgeService";

/**
 * Create a registry with all mock adapters for offline/dev mode.
 */
export function createMockRegistry(): ServiceRegistry {
  // Shared so the negotiation mock can enumerate the quest's tasks. The
  // proposals summary has to produce a row for a task nobody proposed on, and
  // only the quest service knows those exist.
  const mockQuests = new MockQuestService();
  // Shared so a stock-listing clone inherits its anchor's instructions, as
  // the copy-on-clone trigger does in the database.
  const mockQuestInstructions = new MockQuestInstructionsService();
  const mockQuestTemplates = new MockQuestTemplateService(mockQuests, mockQuestInstructions);
  // Shared so an application recorded by the candidates mock leaves the same
  // pending proposals the RPC writes, where the apply buttons read them.
  const mockNegotiations = new MockNegotiationService(mockQuests, mockQuestTemplates);
  // Tasks no registry test applies to: a seeded proposal on a task a test
  // taps would make "the tap left a proposal" pass without the tap writing one.
  mockNegotiations.seedPendingApplications([
    { taskId: "admin-task-002", questId: "admin-quest-001", price: 400 },
    { taskId: "mock-task-401", questId: "mock-quest-004", price: 0 },
  ]);

  return {
    auth: new MockAuthService(),
    quests: mockQuests,
    // Shares the quest store: a stock quest's anchor and its clones are
    // ordinary quests and must be visible to every other mock method.
    questTemplates: mockQuestTemplates,
    bounties: new MockBountyService(),
    users: new MockUserService(),
    payments: new MockPaymentService(),
    storage: new MockStorageService(),
    ai: new MockAIService(),
    workspace: new MockWorkspaceService(),
    negotiations: mockNegotiations,
    admin: new MockAdminService(),
    staffing: new MockStaffingService(),
    candidates: new MockCandidatesService(mockNegotiations),
    compliance: new MockComplianceService(),
    questImages: new MockQuestImageService(),
    attention: new MockAttentionService(),
    channels: new MockChannelService(),
    questInstructions: mockQuestInstructions,
    board: new MockBoardService(),
    organizations: new MockOrganizationService(),
    orgHub: new MockOrgHubService(),
    agents: new MockAgentService(),
    badges: new MockBadgeService(),
    certificates: new MockCertificateService(),
    exchange: new MockExchangeService(),
    activityBadges: new MockActivityBadgeService(),
  };
}

/**
 * Create a registry with Supabase adapters for production mode.
 * When polygonClient is provided, adapters will make on-chain calls
 * before persisting to Supabase (chain-first, DB-second).
 */
export function createSupabaseRegistry(
  polygonClient: PolygonClient | null = null,
): ServiceRegistry {
  return {
    auth: new MockAuthService(), // Auth is handled by Privy directly; mock adapter wraps Zustand
    quests: new SupabaseQuestService(polygonClient),
    questTemplates: new SupabaseQuestTemplateService(),
    bounties: new SupabaseBountyService(polygonClient),
    users: new SupabaseUserService(polygonClient),
    payments: polygonClient
      ? new PolygonPaymentService(polygonClient)
      : new MockPaymentService(),
    storage: new MockStorageService(), // TODO: Replace with SupabaseStorageService when file uploads are needed
    ai: new SupabaseAIService(),
    workspace: new SupabaseWorkspaceService(),
    negotiations: new SupabaseNegotiationService(),
    admin: new SupabaseAdminService(),
    staffing: new SupabaseStaffingService(),
    candidates: new SupabaseCandidatesService(),
    compliance: new SupabaseComplianceService(),
    questImages: new SupabaseQuestImageService(),
    questInstructions: new SupabaseQuestInstructionsService(),
    board: new SupabaseBoardService(),
    organizations: new SupabaseOrganizationService(),
    orgHub: new SupabaseOrgHubService(),
    agents: new SupabaseAgentService(),
    badges: new SupabaseBadgeService(),
    certificates: new SupabaseCertificateService(),
    attention: new SupabaseAttentionService(),
    channels: new SupabaseChannelService(),
    exchange: new SupabaseExchangeService(),
    activityBadges: new SupabaseActivityBadgeService(),
  };
}
